import { useEffect, useState, useRef } from "react";
import { InformationGovernance, useLegacySupport } from "../../../components";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchInformationGovernanceData,
  fetchOverviewreference,
  resetFetchOverviewreference,
  resetInformationGovernanceData,
} from "../store/actions";
import { getData, GROUP_REF_SELETECTED, setData } from "../../../utils/Storage";

interface InformationGovernanceWrapperProps {
  selectedCaseReference: string;
  dsp: string;
  onConfirmed: () => void;
  onClose: () => void;
}

/**
 * InformationGovernanceWrapper component
 * Handles the Information Governance modal flow before allowing access to case summary
 */
const InformationGovernanceWrapper = (props: InformationGovernanceWrapperProps) => {
  const dispatch = useDispatch();
  const { selectedCaseReference, dsp, onConfirmed, onClose } = props;
  
  // Redux state variables
  const informationGovernanceData: any = useSelector<any>((state) => state.informationGovernanceDataCM);
  const overviewreference: any = useSelector<any>((state) => state.overviewreferenceCM);

  // Local state variables
  const [token, setToken] = useState<any>(null);
  const [informationGovernanceModel, setInformationGovernanceModel] = useState<Boolean>(true);
  const [informationGovernanceDataState, setInformationGovernanceDataState] = useState<any>(null);
  
  // Use ref to track initialization to prevent infinite loops
  const hasInitializedRef = useRef<boolean>(false);
  const currentCaseRef = useRef<string | null>(null);

  const legacyToken = useLegacySupport();

  useEffect(() => {
    // Reset initialization when case reference changes
    if (currentCaseRef.current !== selectedCaseReference) {
      currentCaseRef.current = selectedCaseReference;
      hasInitializedRef.current = false;
    }

    // Fetch Information Governance data
    if (legacyToken.token !== null && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setToken(legacyToken.token);

      getData(GROUP_REF_SELETECTED).then((res: any) => {
        if (res !== null && !res.infogov) {
          dispatch(
            fetchInformationGovernanceData({
              token: legacyToken.token,
              dspId: dsp,
              caseReference: selectedCaseReference,
            })
          );
        } else if (res !== null && res.infogov) {
          // Only dispatch fetchOverviewreference if res is not null and has infogov set
          dispatch(fetchOverviewreference(res));
        } else {
          // res is null or invalid - fetch fresh IG data
          dispatch(
            fetchInformationGovernanceData({
              token: legacyToken.token,
              dspId: dsp,
              caseReference: selectedCaseReference,
            })
          );
        }
      });
    }
  }, [legacyToken.token, dsp, selectedCaseReference, dispatch]);

  useEffect(() => {
    // Process received Information Governance data
    try {
      if (informationGovernanceData.isSuccess && informationGovernanceData.data !== null) {
        setInformationGovernanceDataState(informationGovernanceData.data);
        dispatch(resetInformationGovernanceData());
      } else if (informationGovernanceData.isError) {
        // Check if this is a token expiration error - don't show additional toast since handleExpiredToken already shows one
        const errorMessage = informationGovernanceData?.data?.error?.message;
        if (errorMessage !== "Token expired") {
          toast.error(informationGovernanceData?.data?.error?.data || "Failed to load information governance data");
        }
        dispatch(resetInformationGovernanceData());
        // Close the IG wrapper on error so user can retry after re-login
        onClose();
      }
    } catch (e: any) {
      toast.error(e.message);
      console.log(e);
    }
  }, [informationGovernanceData, dispatch, onClose]);

  useEffect(() => {
    // Process received overview references
    if (overviewreference.isSuccess && overviewreference.data !== null) {
      let data = overviewreference.data;

      if (data.accessCaseSummaryFlag === 1) {
        setInformationGovernanceModel(false);
        
        // Update storage with the overview reference data including caseSummaryTypeId
        getData(GROUP_REF_SELETECTED).then((existingData: any) => {
          const updatedData = {
            ...existingData,
            caseSummaryTypeId: data.caseSummaryTypeId,
            infogov: true
          };
          setData(GROUP_REF_SELETECTED, updatedData);
          
          // Notify parent that IG is confirmed
          onConfirmed();
        });
      } else {
        toast.error(data.responseMessage);
      }

      dispatch(resetFetchOverviewreference());
    } else if (overviewreference.isError) {
      dispatch(resetFetchOverviewreference());
    }
  }, [overviewreference, dispatch, onConfirmed]);

  // Submit IG Plugin Authentication
  const __submitIgPluginAuth = (type: string, data: any) => {
    try {
      if (type === "closeForm") {
        window.close();
        setInformationGovernanceModel(false);
        onClose();
      } else if (type === "formSubmit") {
        if (data?.involveCase[4] !== undefined) {
          if (data.involveCase !== null) {
            getData(GROUP_REF_SELETECTED).then((res: any) => {
              if (res === null) {
                toast.error("Please select a case to proceed");
                return;
              }

              let overviewRefReq = {
                ...res,
                token: token,
                dspRoleId: data.involveCase[4].id,
                dspId: dsp,
                url: data.url,
                infogov: true,
              };

              // update the local storage with overview reference
              setData(GROUP_REF_SELETECTED, overviewRefReq);

              /**
               * Dispatch action to fetch overview reference
               **/
              dispatch(fetchOverviewreference(overviewRefReq));
            });
          }
        } else {
          toast.error("Please select an option from the involvement dropdown");
        }
      }
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  // Only render if IG modal needs to be shown
  if (
    informationGovernanceModel &&
    informationGovernanceDataState &&
    informationGovernanceDataState !== null
  ) {
    return (
      <InformationGovernance
        data={informationGovernanceDataState}
        isFetching={overviewreference.isFetching}
        onSubmit={(type: string, res: any) => {
          if (res === null) {
            onClose();
          } else {
            __submitIgPluginAuth(type, res);
          }
        }}
      />
    );
  }

  // Return null when IG is confirmed (parent will render actual content)
  return null;
};

export default InformationGovernanceWrapper;
